import React, { useEffect, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { FiFileText, FiList, FiExternalLink, FiImage, FiPlay, FiUpload, FiTrash2, FiPaperclip } from 'react-icons/fi';

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// an attachment block inside the text (video, picture, PDF): not editable itself, removed with its trash button
export const embedHtml = (icon: JSX.Element, title: string, sub: string) =>
    `<div class="hub-embed" contenteditable="false"><span class="hub-embed-ic">${renderToStaticMarkup(icon)}</span>`
    + `<span class="hub-embed-t"><strong>${esc(title)}</strong><span>${esc(sub)}</span></span>`
    + `<button type="button" class="hub-embed-remove" aria-label="Remove" title="Remove" data-remove="1">${renderToStaticMarkup(<FiTrash2 />)}</button></div>`;

export const sampleVideoEmbed = () => embedHtml(<FiPlay />, 'YouTube video · Junior Miner — Episode 214', 'youtube.com/watch?v=Xk29f… · will play inside the article');

export const paragraphsHtml = (...ps: string[]) => ps.map(p => `<p>${esc(p)}</p>`).join('');

// Article text: a simple editor — B / I / U, heading, bullet list, link, plus picture / video / PDF blocks (spec 409:10781)
// The text lives in the DOM (contentEditable); onChange reports the HTML. initial is read once.
const ArticleEditor: React.FC<{ initial: string; onChange: (html: string) => void }> = ({ initial, onChange }) => {
    const box = useRef<HTMLDivElement>(null);
    // the caret / selection inside the editor, so toolbar buttons and file pickers insert where the writer was
    const range = useRef<Range | null>(null);
    const picture = useRef<HTMLInputElement>(null);
    const pdf = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (box.current) box.current.innerHTML = initial;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const report = () => { if (box.current) onChange(box.current.innerHTML); };

    const remember = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount && box.current?.contains(sel.anchorNode)) range.current = sel.getRangeAt(0);
    };

    // put the caret back where it was (or at the end), then run the command
    const run = (command: string, value?: string) => {
        const el = box.current;
        if (!el) return;
        el.focus();
        const sel = window.getSelection();
        if (sel) {
            let r = range.current;
            if (!r) { r = document.createRange(); r.selectNodeContents(el); r.collapse(false); }
            sel.removeAllRanges();
            sel.addRange(r);
        }
        document.execCommand(command, false, value);
        remember();
        report();
    };

    const heading = () => run('formatBlock', /^h3$/i.test(document.queryCommandValue('formatBlock')) ? 'p' : 'h3');
    const link = () => {
        const url = window.prompt('Link to', 'https://');
        if (url && url !== 'https://') run('createLink', url);
    };
    const insert = (html: string) => run('insertHTML', `${html}<p><br></p>`);

    const onClick = (e: React.MouseEvent) => {
        const remove = (e.target as HTMLElement).closest('[data-remove]');
        if (!remove) return;
        remove.closest('.hub-embed')?.remove();
        report();
    };

    // onMouseDown preventDefault keeps the selection in the text while a toolbar button is pressed
    const tool = (label: string, action: () => void, content: React.ReactNode, className = '') =>
        <button type="button" className={className} title={label} aria-label={label} onMouseDown={e => e.preventDefault()} onClick={action}>{content}</button>;

    return <div className="hub-editor">
        <div className="hub-editor-bar" role="toolbar" aria-label="Formatting">
            {tool('Bold', () => run('bold'), <b>B</b>)}
            {tool('Italic', () => run('italic'), <i>I</i>)}
            {tool('Underline', () => run('underline'), <u>U</u>)}
            <span className="hub-editor-sep" />
            {tool('Heading', heading, <><FiFileText /> Heading</>)}
            {tool('Bullet list', () => run('insertUnorderedList'), <><FiList /> Bullet list</>)}
            {tool('Link', link, <><FiExternalLink /> Link</>)}
            <span className="hub-editor-sep" />
            {tool('Add picture', () => picture.current?.click(), <><FiImage /> Add picture</>, 'hub-editor-add')}
            {tool('Add video', () => insert(sampleVideoEmbed()), <><FiPlay /> Add video</>, 'hub-editor-add')}
            {tool('Attach PDF', () => pdf.current?.click(), <><FiUpload /> Attach PDF</>, 'hub-editor-add')}
        </div>
        <div ref={box} className="hub-editor-text" contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label="Article text"
            data-placeholder="Write the article…"
            onFocus={() => document.execCommand('defaultParagraphSeparator', false, 'p')}
            onInput={report} onKeyUp={remember} onMouseUp={remember} onBlur={remember} onClick={onClick} />
        <input ref={picture} type="file" accept="image/png,image/jpeg" hidden
            onChange={e => { const f = e.target.files?.[0]; if (f) insert(embedHtml(<FiImage />, f.name, 'Picture · shown full width in the article')); e.target.value = ''; }} />
        <input ref={pdf} type="file" accept="application/pdf" hidden
            onChange={e => { const f = e.target.files?.[0]; if (f) insert(embedHtml(<FiPaperclip />, f.name, 'PDF · opens in a new tab')); e.target.value = ''; }} />
    </div>;
};

export default ArticleEditor;
